import { Dispatch } from 'react';
import { BUYSELL, CPM, REGION_NAMES, SELECT, TANSO, TEN } from '../../api/constants';
import {
  Action,
  IReconReportItemCpm,
  IReconReportItemTanso,
  IReconReportTotalVolume,
  IReconReportTotalVolumeTanso,
} from '../../Types/types';
import { UNKNOWN_ERROR } from '../../../CarbonDashboard/constants';

export const decorateColName = (columnName: string) => {
  const valueWithoutSpecialCharacters = columnName.replace(/[^a-zA-Z]/g, ' ');
  const valueAllLowerCase = valueWithoutSpecialCharacters.toLowerCase();
  const valueFirstLetterCaps = valueAllLowerCase[0].toUpperCase() + valueAllLowerCase.substring(1);

  if (valueFirstLetterCaps.replace(' ', '').toUpperCase() === BUYSELL) {
    return 'Buy/Sell';
  } else if (valueFirstLetterCaps.replace(' ', '').toUpperCase() === 'NCFSNAPSHOTID') {
    return 'NCF snapshot id';
  } else {
    return valueFirstLetterCaps;
  }
};

export const snapShotdecorateColName = (columnName: string) => {
  const valueWithoutSpecialCharacters = columnName.replace(/[^a-zA-Z]/g, ' ');
  const valueAllLowerCase = valueWithoutSpecialCharacters.toLowerCase();
  const valueFirstLetterCaps = valueAllLowerCase[0].toUpperCase() + valueAllLowerCase.substring(1);

  if (columnName === 'LAST_MODIFIED_DATE') {
    return 'Last modified date (UTC)';
  } else if (columnName === 'INSERT_DATE') {
    return 'Insert date (UTC)';
  } else if (valueFirstLetterCaps.replace(' ', '').toUpperCase() === 'NCFSNAPSHOTID') {
    return 'NCF snapshot id';
  } else {
    return valueFirstLetterCaps;
  }
};

// converting all TANSO text to CPM in an array of objects
export const convertKeys = (obj: IReconReportItemTanso) => {
  const newObj: IReconReportItemCpm = {
    YEAR: 0,
    MONTH: '',
    NCF_CATEGORY: '',
    CPTY: '',
    BUY_SELL: '',
    CPM_VOLUME: 0,
    ETRM_VOLUME: 0,
    DELTA_VOLUME: 0,
    CPM_COUNT: 0,
    ETRM_COUNT: 0,
  };
  for (const key in obj) {
    if (Object.hasOwn(obj, key)) {
      const newKey = key.replace(/TANSO/g, 'CPM');
      newObj[newKey] = obj[key as keyof IReconReportItemTanso];
    }
  }
  return newObj;
};

// converting all TANSO text to CPM in an object of objects
export const replaceKeyTansoToCpm = (obj: IReconReportTotalVolumeTanso) => {
  const newObj: IReconReportTotalVolume = {
    CPM: {
      TOTAL_VOLUME: 0,
      TOTAL_COUNT: 0,
    },
    ETRM: {
      TOTAL_VOLUME: 0,
      TOTAL_COUNT: 0,
    },
    DELTA: {
      TOTAL_VOLUME: 0,
      TOTAL_COUNT: 0,
    },
  };
  for (const key in obj) {
    if (Object.hasOwn(obj, key)) {
      const newKeyName = key.includes(TANSO) ? key.replace(TANSO, CPM) : key;
      newObj[newKeyName as keyof IReconReportTotalVolume] =
        obj[key as keyof IReconReportTotalVolumeTanso];
    }
  }
  return newObj;
};

export const areStringsEqual = (val: string, col: string) => {
  if (col === undefined) {
    return false;
  }
  return (
    // /[^a-zA-Z]/g is to pick up everything except a-z and A-Z
    val.toLowerCase().replace(/[^a-zA-Z]/g, '') === col.toLowerCase().replace(/[^a-zA-Z]/g, '')
  );
};

export const formatDate = (date: Date): string => {
  const day = date.getDate();
  const month = date.getMonth() + 1; // Months are zero-based
  const year = date.getFullYear();

  // Pad the day and month with leading zeros if necessary
  const formattedDay = day < TEN ? `0${day}` : day.toString();
  const formattedMonth = month < TEN ? `0${month}` : month.toString();

  // Return the formatted date string in dd/mm/yyyy format
  return `${formattedDay}/${formattedMonth}/${year}`;
};

export const showSpinner = (dispatch: Dispatch<Action>) => {
  dispatch({ type: 'setSpinner', payload: true });
};

export const hideSpinner = (dispatch: Dispatch<Action>) => {
  dispatch({ type: 'setSpinner', payload: false });
};

export const getDefaultOptions = () => [{ value: SELECT, label: SELECT }];

export const getCombinedMonths = (quarters: string[]): string[] => {
  const monthsByQuarter = {
    Q1: ['January', 'February', 'March'],
    Q2: ['April', 'May', 'June'],
    Q3: ['July', 'August', 'September'],
    Q4: ['October', 'November', 'December'],
  };

  return quarters.flatMap((quarter) => monthsByQuarter[quarter as keyof typeof monthsByQuarter]);
};

export const isApiResponseEmpty = (
  response: unknown,
  region?: string | string[],
  commodity?: string[],
): boolean => {
  // If region and commodity are provided, both must be present and non-empty, 
  // if either is empty, return false (response is not empty)
  if (!region?.length && !commodity?.length) {
    return false;
  }

  if (!response || typeof response !== 'object') {
    return true;
  }

  if (Array.isArray(response)) {
    return response.length === 0;
  }

  if (region === '') {
    return true; // If region is an empty string, consider the response empty
  }

  // Check if response is an empty object
  if (Object.keys(response).length === 0) {
    return true;
  }

  // Check if response has properties with empty arrays (like {"categories":[],"series":[]})
  const values = Object.values(response);
  if (values.length > 0 && values.every(value => Array.isArray(value) && value.length === 0)) {
    return true;
  }

  return false;
};

export const filterRegionsByAsiaRegion = (
  regionList: string[] | undefined,
  shouldFilter: boolean
): string[] => {
  if (shouldFilter && regionList?.length) {
    return regionList.filter(region => region.startsWith('ASIA-'));
  }
  return [];
}

export const getFilteredRegionsAnalyticsHome = ({ userRegion, asiaRegionList, selectedRegion }: { userRegion: string; asiaRegionList: string[]; selectedRegion: string[]; }) => {
  let filteredRegions: string[] = [];
   if (userRegion && userRegion !== '') {
      if (userRegion === 'ASIA') {
        filteredRegions = asiaRegionList;
      } else {
        filteredRegions = [userRegion];
      }
    } else if (selectedRegion?.length) {
      filteredRegions = selectedRegion;
    } else {
      filteredRegions = Object.values(REGION_NAMES);
    }
  return filteredRegions;
}

export const isCommentsError = (masterComment: string, currentComment: string) => {
  if (masterComment === '' && masterComment !== currentComment) {
    // if master comment is already empty
    return false;
  } else if (masterComment !== currentComment && currentComment !== '') {
    return false;
  } else {
    return true;
  }
};

export const globalApiErrorHandler = (text: string, error: unknown) => {
  console.info(`${text}: ${error instanceof Error ? error.message : 'Unknown error'}`, error);
}

export const errorNotifyMessage = (error: unknown) => {
  return error instanceof Error ? error.message : UNKNOWN_ERROR
}